import type Purchases from 'react-native-purchases'

import type { UserEntitlement } from '../dal/user-dal'
import { queryClient } from '../queries/query-client'
import { queryKeys } from '../queries/query-keys'
import { userEntitlementSyncQueryOptions } from '../queries/user-queries'
import {
  addBreadcrumb,
  captureException,
  captureMessage,
} from '../utils/sentry'
import {
  hasActiveAccess,
  hasResolvedAccess,
  isRevenueCatOwnershipMatch,
} from './entitlement-utils'

type CustomerInfo = Awaited<ReturnType<typeof Purchases.getCustomerInfo>>

interface VerifyEntitlementResult {
  hasAccess: boolean
  backendUserMissing: boolean
}

export type RestorePurchasesResult =
  | { outcome: 'success' }
  | { outcome: 'no_subscription' }
  | {
      outcome: 'ownership_mismatch'
      originalAppUserId: string
      currentRevenueCatId: string | null
    }
  | { outcome: 'error' }

export function resolveRestorePurchasesResult({
  customerInfo,
  entitlement,
  revenueCatId,
}: {
  customerInfo: CustomerInfo
  entitlement: UserEntitlement | null | undefined
  revenueCatId: string | null | undefined
}): Exclude<RestorePurchasesResult, { outcome: 'error' }> {
  if (!hasActiveAccess(customerInfo)) {
    return { outcome: 'no_subscription' }
  }

  if (!isRevenueCatOwnershipMatch({ customerInfo, revenueCatId })) {
    return {
      outcome: 'ownership_mismatch',
      originalAppUserId: customerInfo.originalAppUserId,
      currentRevenueCatId: revenueCatId ?? null,
    }
  }

  if (
    hasResolvedAccess({
      customerInfo,
      entitlement,
      revenueCatId,
    })
  ) {
    return { outcome: 'success' }
  }

  return { outcome: 'no_subscription' }
}

export async function verifyBackendEntitlement(
  customerInfo: CustomerInfo,
  authUserId: string | null,
  revenueCatId: string | null,
  getAccessToken: () => Promise<string | null>,
): Promise<VerifyEntitlementResult> {
  try {
    const accessToken = await getAccessToken()

    if (!accessToken) {
      captureMessage('entitlement_sync_no_token', 'warning', {
        feature: 'entitlement',
      })
      return {
        hasAccess: hasResolvedAccess({
          customerInfo,
          entitlement: null,
          revenueCatId,
        }),
        backendUserMissing: false,
      }
    }

    const userData = await queryClient.fetchQuery(
      userEntitlementSyncQueryOptions(authUserId),
    )
    const hasAccess = hasResolvedAccess({
      customerInfo,
      entitlement: userData.entitlement,
      revenueCatId,
    })

    return {
      hasAccess,
      backendUserMissing: false,
    }
  } catch (syncError: unknown) {
    if (
      syncError &&
      typeof syncError === 'object' &&
      'status' in syncError &&
      syncError.status === 404
    ) {
      addBreadcrumb('entitlement', 'user_not_found_404_backend_missing')
      console.info('User not found (404), resetting cached backend profile')
      queryClient.removeQueries({
        queryKey: userEntitlementSyncQueryOptions(authUserId).queryKey,
        exact: true,
      })
      await queryClient.resetQueries({
        queryKey: queryKeys.user.profile(authUserId),
        exact: true,
      })
      return {
        hasAccess: false,
        backendUserMissing: true,
      }
    }

    captureException(syncError, {
      feature: 'entitlement',
      tags: { flow: 'backend_sync' },
    })
    return {
      hasAccess: hasResolvedAccess({
        customerInfo,
        entitlement: null,
        revenueCatId,
      }),
      backendUserMissing: false,
    }
  }
}
